import type { ViewNode } from "@e308/ux";
import { DURATION_PRESETS, MAPS } from "../content/setup.js";
import { computerTownHelp } from "./app-help-view.js";
import type { AppIntent, AppState } from "./app-source.js";
import { availableMaps } from "./app-state-utils.js";
import { botLobbyFields } from "./bot-lobby-view.js";
import { advisorToggle } from "./guidance-view.js";
import { resultsView } from "./log-view.js";
import { onlineRoomLauncher } from "./online-launcher-view.js";
import { text } from "./view-nodes.js";
export function lobby(state: AppState): ViewNode<AppIntent, string>[] {
  const disabled = state.phase === "starting";
  return [
    ...resultsView(state),
    {
      kind: "section",
      id: "lobby-layout",
      style: { className: "kf-lobby" },
      children: [
        {
          kind: "section",
          id: "practice-panel",
          variant: "card",
          children: [
            { kind: "heading", id: "practice-heading", level: 2, text: "Private practice" },
            text(
              "practice-note",
              disabled
                ? "Preparing your town…"
                : "Your town, your pace. Play computer rivals on this device, with pause and save controls.",
            ),
            {
              kind: "fieldset",
              id: "lobby-fields",
              legend: "Practice match settings",
              children: [...lobbyFields(state)],
            },
          ],
        },
        onlineRoomLauncher(),
      ],
    },
  ];
}

function lobbyFields(state: AppState): ViewNode<AppIntent, string>[] {
  const disabled = state.phase === "starting";
  return [
    {
      kind: "text-input",
      id: "town-name",
      label: "Town name",
      value: state.lobby.townName,
      intent: (value) => ({ kind: "set-town-name", value }),
    },
    {
      kind: "select-input",
      id: "ai-count",
      label: "Computer towns",
      value: String(state.lobby.aiCount),
      options: [1, 2, 3, 5].map((value) => ({
        value: String(value),
        label: String(value),
      })),
      intent: (value) => ({ kind: "set-ai-count", value: Number(value) }),
    },
    ...botLobbyFields(state),
    {
      kind: "select-input",
      id: "map",
      label: "Map",
      value: state.lobby.mapId,
      options: MAPS.filter((map) => availableMaps(state.lobby.aiCount).includes(map.id)).map(
        (map) => ({ value: map.id, label: map.name }),
      ),
      intent: (value) => ({
        kind: "set-map",
        value: value as AppState["lobby"]["mapId"],
      }),
    },
    {
      kind: "select-input",
      id: "duration",
      label: "Shift length",
      value: state.lobby.durationPreset,
      options: DURATION_PRESETS.map((preset) => ({
        value: preset.id,
        label: `${preset.name} · ${preset.limitMinutes} game min`,
      })),
      intent: (value) => ({
        kind: "set-duration",
        value: value as AppState["lobby"]["durationPreset"],
      }),
    },
    {
      kind: "select-input",
      id: "clock-speed",
      label: "Clock speed",
      value: String(state.lobby.clockSpeed),
      options: [
        { value: "1", label: "1× · real time" },
        { value: "2", label: "2× · double speed" },
      ],
      intent: (value) => ({ kind: "set-clock-speed", value: Number(value) as 1 | 2 }),
    },
    computerTownHelp(),
    advisorToggle(state),
    text(
      "shift-summary",
      "Opening peace gives you time to build. If several towns survive the shift, overtime settles the result (up to 10 extra game minutes).",
    ),
    {
      kind: "action",
      id: "start-practice",
      action: {
        id: "start-practice",
        label: disabled ? "Starting…" : "Start practice",
        enabled: !disabled,
        intent: { kind: "start-practice" },
        blockers: [],
      },
    },
  ];
}
